import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { startTokenReveal } from '../../helpers/typewriter-reveal';
import { FormatMessagePipe } from '../../pipes/format-message.pipe';
import { ParentAiAssistantMetaRowComponent } from '../parent-ai-assistant-meta-row/parent-ai-assistant-meta-row.component';
import { ParentAiUserMetaRowComponent } from '../parent-ai-user-meta-row/parent-ai-user-meta-row.component';
import type { ChildOption } from '../../models/child-option.model';
import type { DisplayMessage } from '../../models/display-message.model';

@Component({
  selector: 'app-parent-ai-chat-body',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    TranslatePipe,
    FormatMessagePipe,
    ParentAiAssistantMetaRowComponent,
    ParentAiUserMetaRowComponent,
  ],
  templateUrl: './parent-ai-chat-body.component.html',
  styleUrl: './parent-ai-chat-body.component.scss',
  host: { class: 'chat-body' },
})
export class ParentAiChatBodyComponent {
  readonly #el = inject(ElementRef<HTMLElement>);
  readonly #destroyRef = inject(DestroyRef);

  isVerificationMode = input(false);
  isLoadingConversation = input(false);
  displayMessages = input<DisplayMessage[]>([]);
  isSending = input(false);
  pendingUserMessage = input<{ content: string } | null>(null);
  sendErrorMessage = input<string | null>(null);
  userDisplayName = input('You');
  userAvatarUrl = input<string | null>(null);
  userInitialsSource = input<{ firstName?: string; lastName?: string } | null>(
    null,
  );
  selectedChild = input<ChildOption | null>(null);
  /** POC: token-by-token reveal of the latest assistant reply (frontend only). */
  streamRevealEnabled = input(true);

  streamingSourceContent = signal<string | null>(null);
  streamingVisibleText = signal('');
  isStreamingReply = signal(false);

  /** Thread history frozen at send time so cache updates do not wipe prior messages during stream. */
  #preStreamHistory = signal<DisplayMessage[] | null>(null);

  #cancelReveal: (() => void) | null = null;
  #wasSending = false;
  #revealedKeys = new Set<string>();
  #scrollFollowRaf: number | null = null;
  /** When true, stream/token updates keep the viewport pinned to the latest message. */
  #stickToBottom = true;
  #onScroll: (() => void) | null = null;
  readonly #scrollBottomThresholdPx = 72;

  /** Static prior turns; during stream excludes the assistant reply being revealed. */
  historyMessages = computed(() => {
    const live = this.displayMessages();
    if (!this.isStreamingReply()) {
      return live;
    }

    const prior = this.#preStreamHistory();
    if (!prior) {
      return this.#allButStreamingModel(live);
    }

    const newUser = this.#latestUserTurn(live, prior);
    return newUser ? [...prior, newUser] : prior;
  });

  /** Latest assistant message being token-revealed (separate bubble). */
  streamingMessage = computed((): DisplayMessage | null => {
    if (!this.isStreamingReply()) return null;
    const source = this.streamingSourceContent();
    if (!source) return null;
    const live = this.#lastModelMessage(this.displayMessages());
    if (!live || live.content !== source) {
      return { id: 'streaming-model', role: 'model', content: source };
    }
    return live;
  });

  constructor() {
    afterNextRender(() => {
      const host = this.#el.nativeElement;
      this.#onScroll = () => {
        this.#stickToBottom = this.#isNearBottom(host);
      };
      host.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#followStreamScroll(true);
    });

    effect(() => {
      const sending = this.isSending();
      const messages = this.displayMessages();

      if (sending && !this.#wasSending) {
        this.#stickToBottom = true;
        this.#preStreamHistory.set(
          messages.filter((m) => m.id !== 'pending-user'),
        );
      }

      if (this.#wasSending && !sending && this.streamRevealEnabled()) {
        const lastModel = this.#lastModelMessage(messages);
        if (lastModel) {
          const key = this.#messageRevealKey(lastModel);
          if (!this.#revealedKeys.has(key)) {
            this.#revealedKeys.add(key);
            this.#startReveal(lastModel.content);
          }
        }
      }

      if (!sending && !this.isStreamingReply()) {
        this.#preStreamHistory.set(null);
      }

      this.#wasSending = sending;
    });

    effect(() => {
      this.streamingVisibleText();
      if (this.isStreamingReply()) {
        this.#scheduleScrollFollow();
      }
    });

    effect(() => {
      if (this.isSending()) {
        this.#scheduleScrollFollow();
      }
    });

    this.#destroyRef.onDestroy(() => {
      this.#stopReveal();
      this.#cancelScrollFollow();
      const host = this.#el.nativeElement;
      if (this.#onScroll) {
        host.removeEventListener('scroll', this.#onScroll);
        this.#onScroll = null;
      }
    });
  }

  showTypingIndicator(): boolean {
    return (
      this.isSending() ||
      (this.isStreamingReply() && !this.streamingVisibleText())
    );
  }

  /**
   * @param smooth Use smooth scrolling.
   * @param force When true, always scroll and re-enable stick-to-bottom (e.g. user just sent).
   */
  scrollToBottom(smooth = false, force = false): void {
    if (force) {
      this.#stickToBottom = true;
    }
    if (force || this.#stickToBottom) {
      this.#scrollContainer(smooth);
    }
  }

  #allButStreamingModel(messages: DisplayMessage[]): DisplayMessage[] {
    const source = this.streamingSourceContent();
    if (!source) return messages;
    let lastModelIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role === 'model' && m.content === source) {
        lastModelIdx = i;
        break;
      }
    }
    if (lastModelIdx >= 0) {
      return messages.filter((_, i) => i !== lastModelIdx);
    }
    return messages;
  }

  #latestUserTurn(
    live: DisplayMessage[],
    prior: DisplayMessage[],
  ): DisplayMessage | null {
    if (live.length > prior.length) {
      for (let i = prior.length; i < live.length; i++) {
        const m = live[i];
        if (m?.role === 'user') return m;
      }
    }
    for (let i = live.length - 1; i >= 0; i--) {
      const m = live[i];
      if (m?.role === 'user') return m;
    }
    return null;
  }

  #lastModelMessage(messages: DisplayMessage[]): DisplayMessage | undefined {
    return [...messages]
      .reverse()
      .find((m) => m.role === 'model' && m.content.trim());
  }

  #messageRevealKey(message: DisplayMessage): string {
    return `${message.content.length}:${message.content.slice(0, 48)}`;
  }

  #startReveal(fullText: string): void {
    this.#stopReveal(false);
    this.streamingSourceContent.set(fullText);
    this.streamingVisibleText.set('');
    this.isStreamingReply.set(true);
    this.#stickToBottom = true;
    this.#scheduleScrollFollow();

    this.#cancelReveal = startTokenReveal({
      fullText,
      onUpdate: (visible) => {
        this.streamingVisibleText.set(visible);
      },
      onComplete: () => {
        this.streamingVisibleText.set(fullText);
        this.streamingSourceContent.set(null);
        this.isStreamingReply.set(false);
        this.#preStreamHistory.set(null);
        this.#cancelReveal = null;
        this.#scheduleScrollFollow();
      },
    });
  }

  #stopReveal(clearStreamingFlag = true): void {
    this.#cancelReveal?.();
    this.#cancelReveal = null;
    this.streamingSourceContent.set(null);
    this.#preStreamHistory.set(null);
    if (clearStreamingFlag) {
      this.isStreamingReply.set(false);
    }
  }

  #scheduleScrollFollow(): void {
    if (!this.#stickToBottom) return;
    if (this.#scrollFollowRaf !== null) return;
    this.#scrollFollowRaf = requestAnimationFrame(() => {
      this.#scrollFollowRaf = null;
      this.#followStreamScroll();
    });
  }

  #cancelScrollFollow(): void {
    if (this.#scrollFollowRaf !== null) {
      cancelAnimationFrame(this.#scrollFollowRaf);
      this.#scrollFollowRaf = null;
    }
  }

  #followStreamScroll(force = false): void {
    if (!force && !this.#stickToBottom) return;
    const host = this.#el.nativeElement;
    host.scrollTop = host.scrollHeight;
  }

  #isNearBottom(host: HTMLElement): boolean {
    const distance =
      host.scrollHeight - host.scrollTop - host.clientHeight;
    return distance <= this.#scrollBottomThresholdPx;
  }

  #scrollContainer(smooth: boolean): void {
    const host = this.#el.nativeElement;
    host.scrollTo({
      top: host.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }
}
